import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { Navbar } from "../components/Navbar";
import { VideoCard } from "../components/VideoCard";
import { SponsorRow } from "../components/SponsorRow";
import { useAuth } from "../context/AuthContext";
import { useMyList } from "../context/MyListContext";
import { shareVideo } from "../lib/share";
import { useAutoplayPref, useYouTubeEvents } from "../lib/autoplay";
import { useWatchProgress, rememberGuestSpot } from "../lib/watchProgress";
import { SignUpNudge } from "../components/SignUpNudge";
import { UpNextOverlay } from "../components/UpNextOverlay";
import { Switch } from "../components/ui/switch";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const NUDGE_AFTER_SEC = 60;
const NUDGE_KEY = "qtv_nudge_dismissed";

export default function Watch() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const { isSaved, toggle } = useMyList();
  const navigate = useNavigate();
  const frameRef = useRef(null);
  const [autoplay, setAutoplay] = useAutoplayPref();
  const [ended, setEnded] = useState(false);
  const nextVideo = data?.next;

  useEffect(() => setEnded(false), [id]);
  const progress = useWatchProgress(data?.video, user);
  const [nudge, setNudge] = useState(false);
  const position = useRef(0);
  const onTime = (t, dur) => {
    position.current = t;
    progress.onTime(t, dur);
    if (!user && !nudge && t >= NUDGE_AFTER_SEC && !sessionStorage.getItem(NUDGE_KEY)) setNudge(true);
  };
  const closeNudge = () => {
    sessionStorage.setItem(NUDGE_KEY, "1");
    setNudge(false);
  };
  const goAuth = (path) => {
    rememberGuestSpot(data.video.youtube_id, position.current);
    closeNudge();
    navigate(`${path}?next=${encodeURIComponent(`/watch/${data.video.id}`)}`);
  };
  useYouTubeEvents(
    frameRef,
    { onTime, onEnded: () => { progress.onEnded(); setEnded(true); } },
    !!data && progress.ready,
    data?.video?.id
  );
  const playNext = useCallback(() => nextVideo && navigate(`/watch/${nextVideo.id}`), [nextVideo, navigate]);

  useEffect(() => {
    api.get(`/videos/${id}`).then((r) => setData(r.data)).catch(() => {});
    if (user) {
      api.post("/watch", { video_id: id }).catch(() => {});
    }
  }, [id, user]);

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030305]">
        <div className="w-10 h-10 border-2 border-[#FF2A2A] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const { video, related } = data;
  const saved = isSaved(video.id);

  const handleSave = () => {
    if (!user) return;
    toggle(video.id).then((now) => {
      if (now === true) toast.success("Added to My List");
      else if (now === false) toast("Removed from My List");
    });
  };

  return (
    <div className="min-h-screen bg-[#030305]">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-28 pb-20">
        <motion.div {...fadeUp}>
          <div className="relative">
          <div className="relative aspect-video w-full bg-black border border-white/10 rounded-sm overflow-hidden">
            {progress.ready && <iframe
              key={video.id}
              ref={frameRef}
              data-testid="video-player"
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0&enablejsapi=1${progress.start ? `&start=${progress.start}` : ""}&origin=${encodeURIComponent(window.location.origin)}`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />}
            {ended && autoplay && nextVideo && (
              <UpNextOverlay next={nextVideo} onPlay={playNext} onCancel={() => setEnded(false)} />
            )}
          </div>
            {nudge && !user && !ended && (
              <SignUpNudge onJoin={() => goAuth("/register")} onSignIn={() => goAuth("/login")} onDismiss={closeNudge} />
            )}
          </div>
          <label className="mt-3 inline-flex items-center gap-3 text-sm text-zinc-400 cursor-pointer">
            <Switch data-testid="autoplay-toggle" checked={autoplay} onCheckedChange={(v) => { setAutoplay(v); setEnded(false); }} />
            Autoplay next video {autoplay ? "on" : "off"}
          </label>

          <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#FF2A2A] mb-2">{video.category}</p>
              <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">{video.title}</h1>
              <p className="mt-3 text-zinc-400 max-w-2xl leading-relaxed">{video.description}</p>
            </div>
            <Link
              to={`/channel/${video.channel}`}
              data-testid="back-to-channel"
              className="text-sm border border-white/20 hover:bg-white/10 px-5 py-2.5 rounded-sm transition-colors"
            >
              View Channel
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {user && (
              <button
                data-testid="watch-mylist-button"
                onClick={handleSave}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-sm font-semibold transition-colors ${
                  saved
                    ? "bg-white/10 border border-[#FF2A2A] text-white"
                    : "bg-white/10 border border-white/20 hover:bg-white/20 text-white"
                }`}
              >
                {saved ? (
                  <>
                    <Check className="w-5 h-5 text-[#FF2A2A]" strokeWidth={3} /> In My List
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" strokeWidth={2.5} /> Add to My List
                  </>
                )}
              </button>
            )}
            <button
              data-testid="watch-share-button"
              onClick={() => shareVideo(video)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm font-semibold bg-white/10 border border-white/20 hover:bg-white/20 text-white transition-colors"
            >
              <Share2 className="w-5 h-5" strokeWidth={2.5} /> Share
            </button>
          </div>
          {!user && (
            <p className="mt-4 text-sm text-zinc-500">
              <Link to="/register" className="text-[#FF2A2A] hover:underline">Sign up free</Link> to save your watch history.
            </p>
          )}
        </motion.div>

        <div className="mt-12">
          <SponsorRow slug={video.channel} />
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display text-2xl uppercase tracking-tight mb-5">Up Next</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
